/**
 * PhaseStatusBadge Component
 *
 * Visual indicator for job execution phase status (extraction, load, transform).
 */

import React from "react";
import type { ExtractionStatus, LoadStatus, TransformStatus } from "../../../types/elt-job-execution.types";

type PhaseStatus = ExtractionStatus | LoadStatus | TransformStatus;

interface PhaseStatusBadgeProps {
  status?: PhaseStatus;
  phase: "extraction" | "load" | "transform";
}

// Map all possible phase statuses to display config
const statusConfig: Record<string, { label: string; className: string; pulse: boolean }> = {
  // Common statuses
  pending: {
    label: "Pending",
    className: "bg-gray-100 text-gray-600 border-gray-200",
    pulse: false,
  },
  succeeded: {
    label: "Succeeded",
    className: "bg-green-100 text-green-800 border-green-200",
    pulse: false,
  },
  failed: {
    label: "Failed",
    className: "bg-red-100 text-red-800 border-red-200",
    pulse: false,
  },
  skipped: {
    label: "Skipped",
    className: "bg-gray-100 text-gray-500 border-gray-200",
    pulse: false,
  },
  // Extraction statuses
  running: {
    label: "Running",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    pulse: true,
  },
  // Load statuses
  downloading: {
    label: "Downloading",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    pulse: true,
  },
  creating_tables: {
    label: "Creating Tables",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    pulse: true,
  },
  cleaning: {
    label: "Cleaning",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    pulse: true,
  },
  loading: {
    label: "Loading",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    pulse: true,
  },
  // Transform statuses
  staging: {
    label: "Staging",
    className: "bg-purple-100 text-purple-800 border-purple-200",
    pulse: true,
  },
  production: {
    label: "Production",
    className: "bg-purple-100 text-purple-800 border-purple-200",
    pulse: true,
  },
};

const phaseLabels: Record<string, string> = {
  extraction: "Extraction",
  load: "Load",
  transform: "Transform",
};

export const PhaseStatusBadge: React.FC<PhaseStatusBadgeProps> = ({ status, phase }) => {
  const config = status ? statusConfig[status] || statusConfig.pending : statusConfig.pending;

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-500 w-20">{phaseLabels[phase]}:</span>
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}>
        {config.pulse && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
          </span>
        )}
        {config.label}
      </span>
    </div>
  );
};

/**
 * PhaseStatusTimeline Component
 *
 * Displays all three phases in a timeline format with visual indicators.
 */
interface PhaseStatusTimelineProps {
  extractionStatus?: ExtractionStatus;
  loadStatus?: LoadStatus;
  transformStatus?: TransformStatus;
  currentStep?: string;
}

export const PhaseStatusTimeline: React.FC<PhaseStatusTimelineProps> = ({ extractionStatus, loadStatus, transformStatus, currentStep }) => {
  const phases = [
    { phase: "extraction" as const, status: extractionStatus, label: "Extraction" },
    { phase: "load" as const, status: loadStatus, label: "Load" },
    { phase: "transform" as const, status: transformStatus, label: "Transform" },
  ];

  const getStepStatus = (status?: PhaseStatus): "completed" | "active" | "pending" | "failed" => {
    if (!status || status === "pending") return "pending";
    if (status === "succeeded") return "completed";
    if (status === "failed") return "failed";
    return "active";
  };

  const getStepStyle = (stepStatus: "completed" | "active" | "pending" | "failed") => {
    switch (stepStatus) {
      case "completed":
        return {
          circle: "bg-green-500 border-green-500",
          line: "bg-green-500",
          text: "text-green-700",
        };
      case "active":
        return {
          circle: "bg-blue-500 border-blue-500 animate-pulse",
          line: "bg-gray-300",
          text: "text-blue-700",
        };
      case "failed":
        return {
          circle: "bg-red-500 border-red-500",
          line: "bg-red-300",
          text: "text-red-700",
        };
      default:
        return {
          circle: "bg-gray-200 border-gray-300",
          line: "bg-gray-200",
          text: "text-gray-500",
        };
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {phases.map((p, index) => {
          const stepStatus = getStepStatus(p.status);
          const style = getStepStyle(stepStatus);
          const config = p.status ? statusConfig[p.status] : statusConfig.pending;

          return (
            <React.Fragment key={p.phase}>
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${style.circle}`}>
                  {stepStatus === "completed" && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {stepStatus === "failed" && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {stepStatus === "active" && <span className="w-3 h-3 rounded-full bg-white" />}
                  {stepStatus === "pending" && <span className="w-2 h-2 rounded-full bg-gray-400" />}
                </div>
                <div className={`mt-2 text-sm font-medium ${style.text}`}>{p.label}</div>
                <div className={`text-xs ${config.className} px-2 py-0.5 rounded mt-1`}>{config.label}</div>
              </div>

              {/* Connector line (except for last item) */}
              {index < phases.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${style.line}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Current step description */}
      {currentStep && (
        <div className="mt-4 text-sm text-gray-600 text-center">
          Current step: <span className="font-medium">{currentStep}</span>
        </div>
      )}
    </div>
  );
};
